import ImageHelper, {
  Container as ImageHelperContainer,
} from "@components/ImageHelper"
import colors from "@constants/colors"
import { resetButtonStyles } from "@constants/styles"
import {
  smallcapsBold13Styles,
  smallcapsBold14Styles,
} from "@constants/typography"
import { DesktopOnly } from "@utils/MediaQuery"
import * as R from "ramda"
import React from "react"
import styled from "styled-components"

import DesktopPDPMuxPlayer from "./DesktopMuxPlayer"
import { MuxVideo as MuxVideoType } from "./index"
import ZoomedImageGallery from "./ZoomedImageGallery"

const Container = styled.div`
  flex-direction: column;
  display: flex;
`

const ImageBlock = styled.div``

const Row = styled.div`
  flex-direction: row;
  justify-content: space-between;
  display: flex;
  margin: 24px 0;
`

const MainImage = styled.div`
  ${ImageHelperContainer} {
    width: 100%;
    height: calc(6 / 12 * 100vw * 0.8);
    margin-right: 0;
    background: ${colors.backgroundGray};
    cursor: zoom-in;

    img {
      object-fit: contain;
    }
  }
`

const MainActionImage = styled(MainImage)`
  ${ImageHelperContainer} {
    img {
      object-fit: cover;
    }
  }
`

const SecondaryImage = styled.div`
  ${ImageHelperContainer} {
    height: auto;
    background: ${colors.backgroundGray};
    cursor: zoom-in;

    img {
      object-fit: contain;
    }
  }
`

const SecondaryActionImage = styled(SecondaryImage)`
  ${ImageHelperContainer} {
    img {
      object-fit: cover;
    }
  }
`

const Filler = styled.div`
  height: 5vw;
`

const ActionHeader = styled.div`
  ${smallcapsBold14Styles}
  margin: 63px 0 29px;
  font-size: 16px;
  letter-spacing: 0.5px;
  line-height: 24px;
  color: ${colors.black};
`

const ClickableImageWrapperButton = styled.div<{ secondary?: boolean }>`
  ${resetButtonStyles}

  ${({ secondary }) =>
    secondary
      ? `
      width: calc(50% - 16px);
      height: 100%;
        `
      : `
    width: 100%;
    height: 100%;
  `}
`

const renderVariantImageBlock = (
  images: Array<MuxVideoType | string>,
  key: number,
  setMediaToScrollTo: (media: MuxVideoType | string) => void,
  toggleIsZoomedImageGalleryOpen: (isZoomedImageGalleryOpen?: boolean) => void
) => {
  const main = images[0]
  const secondary1 = images[1]
  const secondary2 = images[2]
  return (
    <ImageBlock key={key}>
      {main && (
        <ClickableImageWrapperButton
          aria-label="Zoom in on product image"
          onClick={() => {
            toggleIsZoomedImageGalleryOpen(true)
            setMediaToScrollTo(main)
          }}
          role="button"
        >
          <MainImage>
            <ImageHelper alt="Product Image" src={main as string} />
          </MainImage>
        </ClickableImageWrapperButton>
      )}
      <Row>
        {secondary1 && (
          <ClickableImageWrapperButton
            aria-label="Zoom in on product image"
            onClick={() => {
              toggleIsZoomedImageGalleryOpen(true)
              setMediaToScrollTo(secondary1)
            }}
            role="button"
            secondary
          >
            <SecondaryImage>
              <ImageHelper
                alt="Product Image"
                src={secondary1 as string}
              />{" "}
            </SecondaryImage>
          </ClickableImageWrapperButton>
        )}
        {secondary2 && typeof secondary2 === "string" && (
          <ClickableImageWrapperButton
            aria-label="Zoom in on product image"
            onClick={() => {
              toggleIsZoomedImageGalleryOpen(true)
              setMediaToScrollTo(secondary2)
            }}
            role="button"
            secondary
          >
            <SecondaryImage>
              <ImageHelper alt="Product Image" src={secondary2} />{" "}
            </SecondaryImage>
          </ClickableImageWrapperButton>
        )}
        {/* If not typeof string, we know type is MuxVideoType */}
        {secondary2 && typeof secondary2 !== "string" && (
          <ClickableImageWrapperButton
            aria-label="Zoom in on product image"
            onClick={() => {
              toggleIsZoomedImageGalleryOpen(true)
              setMediaToScrollTo(secondary2)
            }}
            role="button"
            secondary
          >
            <DesktopPDPMuxPlayer
              autoplayVideo
              hideControls
              muxVideo={secondary2}
            />
          </ClickableImageWrapperButton>
        )}
      </Row>
    </ImageBlock>
  )
}

const renderVariantImages = (
  images: Array<string>,
  toggleIsZoomedImageGalleryOpen: (isZoomedImageGalleryOpen?: boolean) => void,
  setMediaToScrollTo: (media: MuxVideoType | string) => void,
  imageGalleryMuxVideo?: Array<MuxVideoType>
) => {
  let galleryMedia: Array<MuxVideoType | string>
  if (images.length > 2 && imageGalleryMuxVideo) {
    galleryMedia = [
      ...images.slice(0, 2),
      imageGalleryMuxVideo[0],
      ...images.slice(2),
    ]
  } else {
    galleryMedia = [...images]
  }
  const chunkedImages = R.splitEvery(3, galleryMedia)
  return chunkedImages.map((chunk, index) => {
    return renderVariantImageBlock(
      chunk as any,
      index,
      setMediaToScrollTo,
      toggleIsZoomedImageGalleryOpen
    )
  })
}

const renderActionImageBlock = (
  media: Array<MuxVideoType | string>,
  key: number,
  setMediaToScrollTo: (media: MuxVideoType | string) => void,
  toggleIsZoomedImageGalleryOpen: (isZoomedImageGalleryOpen?: boolean) => void
) => {
  const secondary1 = media[0]
  const secondary2 = media[1]
  const main = media[2]
  return (
    <ImageBlock key={key}>
      <Row>
        {secondary1 && typeof secondary1 === "string" && (
          <ClickableImageWrapperButton
            aria-label="Zoom in on product image"
            onClick={() => {
              toggleIsZoomedImageGalleryOpen(true)
              setMediaToScrollTo(secondary1)
            }}
            role="button"
            secondary
          >
            <SecondaryActionImage>
              <ImageHelper alt="Product Action Image" src={secondary1} />
            </SecondaryActionImage>
          </ClickableImageWrapperButton>
        )}
        {secondary1 && typeof secondary1 !== "string" && (
          <ClickableImageWrapperButton
            aria-label="Zoom in on product image"
            onClick={() => {
              toggleIsZoomedImageGalleryOpen(true)
              setMediaToScrollTo(secondary1)
            }}
            role="button"
            secondary
          >
            <DesktopPDPMuxPlayer
              disablePlayback
              hideControls
              muxVideo={secondary1}
            />
          </ClickableImageWrapperButton>
        )}
        {secondary2 && typeof secondary2 === "string" && (
          <ClickableImageWrapperButton
            aria-label="Zoom in on product image"
            onClick={() => {
              toggleIsZoomedImageGalleryOpen(true)
              setMediaToScrollTo(secondary2)
            }}
            role="button"
            secondary
          >
            <SecondaryActionImage>
              <ImageHelper alt="Product Action Image" src={secondary2} />
            </SecondaryActionImage>
          </ClickableImageWrapperButton>
        )}
        {secondary2 && typeof secondary2 !== "string" && (
          <ClickableImageWrapperButton
            aria-label="Zoom in on product image"
            onClick={() => {
              toggleIsZoomedImageGalleryOpen(true)
              setMediaToScrollTo(secondary2)
            }}
            role="button"
            secondary
          >
            <DesktopPDPMuxPlayer
              disablePlayback
              hideControls
              muxVideo={secondary2}
            />
          </ClickableImageWrapperButton>
        )}
      </Row>
      {main && typeof main === "string" && (
        <MainActionImage
          onClick={() => {
            toggleIsZoomedImageGalleryOpen(true)
            setMediaToScrollTo(main)
          }}
        >
          <ImageHelper src={main} />
        </MainActionImage>
      )}
      {main && typeof main !== "string" && (
        <MainActionImage
          onClick={() => {
            toggleIsZoomedImageGalleryOpen(true)
            setMediaToScrollTo(main)
          }}
        >
          <DesktopPDPMuxPlayer
            disablePlayback
            fullWidth
            hideControls
            muxVideo={main}
          />
        </MainActionImage>
      )}
    </ImageBlock>
  )
}

const renderActionImages = (
  images: Array<string>,
  toggleIsZoomedImageGalleryOpen: (isZoomedImageGalleryOpen?: boolean) => void,
  setMediaToScrollTo: (mediaItem: MuxVideoType | string) => void,
  lifestyleMuxVideos?: Array<MuxVideoType>
) => {
  let lifestyleMedia: Array<MuxVideoType | string>
  if (images.length > 2 && lifestyleMuxVideos) {
    lifestyleMedia = [
      ...images.slice(0, 2),
      lifestyleMuxVideos[0],
      ...images.slice(2),
      ...lifestyleMuxVideos.slice(1),
    ]
  } else {
    lifestyleMedia = [...images, ...lifestyleMuxVideos!]
  }

  const chunkedMedia = R.splitEvery(3, lifestyleMedia)

  return chunkedMedia.map((chunk, index) => {
    return renderActionImageBlock(
      chunk as any,
      index,
      setMediaToScrollTo,
      toggleIsZoomedImageGalleryOpen
    )
  })
}

interface DesktopProductImagesProps {
  currentVariant: {
    images: Array<string>
  }
  imageGalleryMuxVideos?: Array<MuxVideoType>
  images: Array<string>
  lifestyleMuxVideos?: Array<MuxVideoType>
}

const DesktopProductImages: React.FC<DesktopProductImagesProps> = (props) => {
  const { currentVariant } = props

  const variantImages = currentVariant.images || []
  const actionImages = props.images || []
  const lifestyleMuxVideos = props.lifestyleMuxVideos || []
  const imageGalleryMuxVideos = props.imageGalleryMuxVideos || []

  const [isZoomedImageGalleryOpen, setIsZoomedImageGalleryOpen] =
    React.useState(false)

  const toggleIsZoomedImageGalleryOpen = (zoomedImageGalleryOpen?: boolean) =>
    setIsZoomedImageGalleryOpen(
      zoomedImageGalleryOpen || !isZoomedImageGalleryOpen
    )

  const [mediaToScrollTo, setMediaToScrollTo] = React.useState<
    MuxVideoType | string
  >()

  const zoomedGalleryMedia = [
    ...variantImages,
    ...actionImages,
    ...imageGalleryMuxVideos,
    ...lifestyleMuxVideos,
  ]

  return (
    <DesktopOnly>
      {isZoomedImageGalleryOpen && (
        <ZoomedImageGallery
          media={zoomedGalleryMedia}
          mediaScrollToItem={mediaToScrollTo}
          toggleIsZoomedImageGalleryOpen={toggleIsZoomedImageGalleryOpen}
        />
      )}
      <Container>
        <Container>
          {renderVariantImages(
            variantImages,
            toggleIsZoomedImageGalleryOpen,
            setMediaToScrollTo,
            props.imageGalleryMuxVideos!
          )}
        </Container>
        {variantImages.length === 1 && <Filler />}
        {(actionImages.length > 0 || lifestyleMuxVideos?.length > 0) && (
          <Container>
            <ActionHeader>See it in action</ActionHeader>
            {renderActionImages(
              actionImages,
              toggleIsZoomedImageGalleryOpen,
              setMediaToScrollTo,
              props.lifestyleMuxVideos || []
            )}
          </Container>
        )}
      </Container>
    </DesktopOnly>
  )
}

export default DesktopProductImages
